import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Check, Clock, Doc, Euro, Partner, Shield } from "@/components/landing/icons";
import { Seo } from "@/components/landing/Seo";
import { FinalCta, LandingHero, LandingNav, ReviewsFaq, SegmentCards, Who, d, useReveal } from "@/components/landing/Sections";
import { CABINET_FAQ, CAB_IMG, CEE_FLOW, FIGURES, PROCESS, PROMISES, ROLE_DO, ROLE_DONT, WHO_DOES } from "@/pages/cabinetContent";

const ICONS = [Doc, Euro, Partner, Shield, Clock, Check];

const Figures = () => (
  <div className="lp-band"><div className="lp-wrap" data-testid="cabinet-figures">
    {FIGURES.map(([v, l], i) => <div key={v} className="lp-kpi lp-cab-fig lp-rv" style={d(i * 60)}><div><strong>{v}</strong><small>{l}</small></div></div>)}
  </div></div>
);

const Role = () => (
  <section className="lp-section" id="role">
    <div className="lp-wrap lp-stakes">
      <div className="lp-bezel lp-rv"><div className="lp-core lp-stakebox" data-testid="cabinet-role-do">
        <span className="lp-eyebrow">Notre rôle</span>
        <h2>Un courtier qui <em>finance</em> et coordonne</h2>
        <p className="lp-cab-p">Entre vous, les financeurs CEE et l'installateur, Airthermo pilote tout ce qui rend votre projet possible.</p>
        <ul>{ROLE_DO.map((t) => <li key={t}><Check />{t}</li>)}</ul>
        <div className="lp-fichebox" data-testid="cabinet-role-proof"><strong>Des centaines de projets</strong><p>accompagnés en B2B et B2C : copropriétés, bailleurs, entreprises, collectivités et particuliers.</p><span><Shield />Dossiers conformes au Pôle National CEE</span></div>
      </div></div>
      <div className="lp-bezel lp-rv" style={d(120)}><div className="lp-core lp-stakebox lp-cab-dark" data-testid="cabinet-role-dont">
        <span className="lp-eyebrow">Ce que nous ne faisons pas</span>
        <h2>Pas de <em>mauvaise</em> surprise</h2>
        <ol>{ROLE_DONT.map((t, i) => <li key={t}><span className={`lp-n lp-c${i}`}>{i + 1}</span>{t}</li>)}</ol>
        <div className="lp-cab-photo"><img src={CAB_IMG.plans} alt="Conseillers étudiant les plans d'un bâtiment" loading="lazy" /></div>
      </div></div>
    </div>
  </section>
);

const Process = () => (
  <section className="lp-section lp-how" id="methode">
    <div className="lp-wrap lp-cab-process">
      <div className="lp-head">
        <div><span className="lp-eyebrow lp-rv">Notre process</span><h2 className="lp-rv" style={d(60)}>6 étapes, du premier appel à la <em>prime</em></h2></div>
        <p className="lp-rv" style={d(120)}>Un déroulé clair, des délais indicatifs et un livrable concret à chaque étape.</p>
      </div>
      <ol className="lp-cab-timeline" data-testid="cabinet-process">
        {PROCESS.map(([t, who, when, x, items], i) => (
          <li key={t} className="lp-cab-step lp-rv" style={d((i % 3) * 80)} data-testid={`cabinet-step-${i + 1}`}>
            <span className={`lp-n lp-c${i % 4}`}>{i + 1}</span>
            <div className="lp-bezel"><div className="lp-core">
              <span className="lp-cab-meta"><span className="lp-cab-when">{when}</span><Who who={who} /></span>
              <h3>{t}</h3>
              <p>{x}</p>
              <ul>{items.map((it) => <li key={it}><Check />{it}</li>)}</ul>
            </div></div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const WhoDoes = () => (
  <section className="lp-section" id="qui-fait-quoi">
    <div className="lp-wrap">
      <div className="lp-head">
        <div><span className="lp-eyebrow lp-rv">Qui fait quoi ?</span><h2 className="lp-rv" style={d(60)}>Deux experts, des rôles <em>clairs</em></h2></div>
        <p className="lp-rv" style={d(120)}>Votre conseiller Airthermo reste votre point de contact principal et fait le lien avec l'installateur.</p>
      </div>
      <div className="lp-cab-who" data-testid="cabinet-who-does">
        {WHO_DOES.map(([t, s, items], i) => (
          <div key={t} className={`lp-bezel lp-rv${i ? "" : " lp-cab-who-main"}`} style={d(i * 120)} data-testid={`cabinet-who-${i ? "installer" : "airthermo"}`}><div className="lp-core">
            <span className={`lp-who lp-who-${i ? "i" : "a"}`}>{i ? "Chantier" : "Point de contact principal"}</span>
            <h3>{t}</h3><small>{s}</small>
            <ul>{items.map((it) => <li key={it}><Check />{it}</li>)}</ul>
          </div></div>
        ))}
      </div>
    </div>
  </section>
);

const Cee = () => (
  <section className="lp-section" id="cee">
    <div className="lp-wrap">
      <div className="lp-box lp-cab-cee lp-rv" data-testid="cabinet-cee">
        <div className="lp-txt">
          <span className="lp-eyebrow">La prime CEE</span>
          <h2>Comment votre pompe à chaleur est <em>financée</em></h2>
          <ol className="lp-cab-flow">
            {CEE_FLOW.map(([t, x], i) => <li key={t}><span className="lp-cab-num">0{i + 1}</span><div><strong>{t}</strong><p>{x}</p></div></li>)}
          </ol>
          <div className="lp-row"><div><strong>BAR-TH-179</strong><small>Résidentiel collectif</small></div><div><strong>BAT-TH-163</strong><small>Tertiaire</small></div></div>
        </div>
        <div className="lp-fph"><img src={CAB_IMG.pac} alt="Pompes à chaleur installées devant un bâtiment" loading="lazy" /></div>
      </div>
    </div>
  </section>
);

const Promises = () => (
  <section className="lp-section lp-cab-promsec" id="engagements">
    <div className="lp-wrap">
      <div className="lp-head"><div><span className="lp-eyebrow lp-rv">Nos engagements</span><h2 className="lp-rv" style={d(60)}>Ce sur quoi vous pouvez <em>compter</em></h2></div></div>
      <div className="lp-cab-promises" data-testid="cabinet-promises">
        {PROMISES.map(([t, x], i) => {
          const Icon = ICONS[i];
          return <div key={t} className="lp-bezel lp-rv" style={d((i % 3) * 60)}><div className="lp-core"><span className={`lp-k lp-c${i % 4}`}><Icon /></span><h3>{t}</h3><p>{x}</p></div></div>;
        })}
      </div>
    </div>
  </section>
);

export default function CabinetPage() {
  useReveal("cabinet");
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="lp" data-testid="cabinet-page">
      <Seo path="/le-cabinet" />
      <LandingNav />
      <LandingHero
        image={CAB_IMG.hero} alt="L'équipe Airthermo en réunion de projet" source="cabinet"
        top={<Link to="/" className="lp-back" data-testid="cabinet-back"><ArrowLeft size={14} />Accueil</Link>}
        eyebrow="Le cabinet Airthermo"
        title={<span data-testid="cabinet-title">Votre courtier en énergie, du diagnostic à la <em>prime</em>.</span>}
        lead="Airthermo est un cabinet de courtage en énergie : nous finançons votre pompe à chaleur grâce aux Certificats d'Économies d'Énergie et coordonnons le projet aux côtés de votre installateur partenaire certifié RGE."
        proofs={["Des centaines de projets B2B et B2C", "Installateurs RGE", "0 € de frais de courtage"]}
      />
      <Figures />
      <Role />
      <WhoDoes />
      <Process />
      <Cee />
      <Promises />
      <ReviewsFaq faq={CABINET_FAQ} title={<>Tout savoir sur le <em>cabinet</em></>} />
      <SegmentCards title={<>Les bâtiments que nous <em>accompagnons</em></>} />
      <FinalCta source="cabinet" />
    </div>
  );
}
